import React, { useState, useMemo } from 'react';
import {
  Box,
  FormControl,
  InputAdornment,
  TextField,
  Typography,
  CircularProgress
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useTheme } from 'styled-components';
import { RightSideCards } from '../LeadDetails/LeadDetailsEdit/leadDetailsEdit.styles';
import useCoreKnowledge from '../../hooks/useCoreKnowledge';
import CoreKnowledgeList from './CoreKnowledgeList';

const filterKnowledgeData = (groupedData, searchTerm) => {
  if (!searchTerm || !groupedData) {
    return groupedData;
  }

  const normalizedSearch = searchTerm.toLowerCase().trim();
  const filtered = {};

  Object.entries(groupedData).forEach(([category, items]) => {
    const matchingItems = items.filter(
      item =>
        item.question?.toLowerCase().includes(normalizedSearch) ||
        item.answer?.toLowerCase().includes(normalizedSearch)
    );

    if (matchingItems.length > 0) {
      filtered[category] = matchingItems;
    }
  });

  return Object.keys(filtered).length > 0 ? filtered : null;
};

const CoreKnowledge = props => {
  const { apartmentId, tenantId } = props;
  const [searchTerm, setSearchTerm] = useState('');

  const theme = useTheme();

  const {
    data: knowledgeData,
    isLoading,
    isError
  } = useCoreKnowledge({
    clientId: tenantId,
    communityId: apartmentId
  });

  const filteredData = useMemo(() => {
    return filterKnowledgeData(knowledgeData, searchTerm);
  }, [knowledgeData, searchTerm]);

  const handleSearchChange = e => {
    setSearchTerm(e.target.value);
  };

  const hasData = knowledgeData && Object.keys(knowledgeData).length > 0;

  return (
    <RightSideCards className="right-side-cards">
      <Box
        sx={{
          mb: 2,
          position: 'relative',
          display: 'flex',
          flexDirection: 'row'
        }}
      >
        <Typography className="card-title" sx={{ color: theme?.text?.heading }}>
          Knowledge Base
        </Typography>
      </Box>

      <FormControl fullWidth sx={{ mb: 1 }}>
        <TextField
          value={searchTerm}
          onChange={handleSearchChange}
          placeholder="Search questions..."
          sx={{
            '& .MuiInputBase-root': {
              paddingY: 0.5
            }
          }}
          variant="outlined"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 18, color: '#666' }} />
              </InputAdornment>
            )
          }}
        />
      </FormControl>

      {isLoading ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            py: 2,
            color: theme?.text?.primary
          }}
        >
          <Typography sx={{ fontSize: '14px', fontWeight: 500, mb: 1 }}>
            Loading core knowledge
          </Typography>
          <CircularProgress size={24} />
        </Box>
      ) : isError ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            py: 2,
            color: theme?.text?.error || '#ff4d4f',
            textAlign: 'center'
          }}
        >
          <Typography sx={{ fontSize: '14px', fontWeight: 500 }}>
            Failed to load core knowledge
          </Typography>
          <Typography sx={{ fontSize: '12px', mt: 0.625, opacity: 0.8 }}>
            Please try refreshing the page or contact support if the issue
            persists.
          </Typography>
        </Box>
      ) : hasData ? (
        <CoreKnowledgeList
          groupedData={searchTerm ? filteredData : knowledgeData}
          searchTerm={searchTerm}
        />
      ) : (
        <Typography
          sx={{ fontSize: '14px', color: theme?.text?.primary, mt: 1.25 }}
        >
          No knowledge available for this community.
        </Typography>
      )}
    </RightSideCards>
  );
};

export default CoreKnowledge;
